// Места отобранных норм на карте раздела «Правила».
//
// Свой источник, а не общий слой запретов: тумблер «Промысловые районы» в том
// же разделе рисует ВСЕ запреты кадра, а здесь — только отобранные, и
// выбранная норма подсвечена. Делить один источник значило бы, что тумблер
// гасит результат фильтра.

import type maplibregl from 'maplibre-gl'
import { banColor } from '../map/bans'

export const RULES_SOURCE_ID = 'rules-places'
export const RULES_LAYER_IDS = ['rules-places-fill', 'rules-places-line', 'rules-places-pick']

/** Фильтр «это выбранная норма»; без выбора не подсвечено ничего. */
export function pickFilter(code: string | null): unknown[] {
  return ['==', ['get', 'code'], code ?? '']
}

export function applyRulesLayer(map: maplibregl.Map, data: GeoJSON.FeatureCollection,
                                selected: string | null): void {
  const src = map.getSource(RULES_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (src) {
    src.setData(data)
  } else {
    const color = banColor()
    map.addSource(RULES_SOURCE_ID, { type: 'geojson', data })
    map.addLayer({ id: RULES_LAYER_IDS[0], type: 'fill', source: RULES_SOURCE_ID,
      paint: { 'fill-color': color, 'fill-opacity': 0.12 } })
    map.addLayer({ id: RULES_LAYER_IDS[1], type: 'line', source: RULES_SOURCE_ID,
      paint: { 'line-color': color, 'line-width': 1, 'line-opacity': 0.7 } })
    map.addLayer({ id: RULES_LAYER_IDS[2], type: 'line', source: RULES_SOURCE_ID,
      paint: { 'line-color': color, 'line-width': 2.6 } })
  }
  map.setFilter(RULES_LAYER_IDS[2], pickFilter(selected) as never)
}

/** Рамка геометрии фичи — чтобы подвести карту к выбранной норме. */
export function featureBounds(f: GeoJSON.Feature): [number, number, number, number] | null {
  const g = f.geometry
  if (!g || (g.type !== 'Polygon' && g.type !== 'MultiPolygon')) return null
  const polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity
  for (const poly of polys) for (const ring of poly) for (const [x, y] of ring) {
    w = Math.min(w, x); e = Math.max(e, x); s = Math.min(s, y); n = Math.max(n, y)
  }
  return Number.isFinite(w) ? [w, s, e, n] : null
}

// ---- окрестность клика -----------------------------------------------------

export const POINT_SOURCE_ID = 'rules-point'
/** Цвет точки и её окрестности — тот же, что у маркера точки. */
export const POINT_COLOR = '#e11d48'
export const POINT_LAYER_IDS = ['rules-point-fill', 'rules-point-line']

/** Круг радиуса `radiusM` вокруг точки — многоугольником: окрестность, в
 *  которой сервер брал свойства места, должна быть видна, иначе «у края места»
 *  в списке не к чему привязать глазами. */
export function circlePolygon(lon: number, lat: number, radiusM: number, n = 64): GeoJSON.Feature {
  const dLat = radiusM / 111_320
  const dLon = dLat / Math.max(Math.cos((lat * Math.PI) / 180), 0.01)
  const ring: [number, number][] = []
  for (let i = 0; i <= n; i++) {
    const a = (2 * Math.PI * i) / n
    ring.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)])
  }
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }
}

export function applyPointLayer(map: maplibregl.Map, data: GeoJSON.FeatureCollection): void {
  const src = map.getSource(POINT_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (src) {
    src.setData(data)
    return
  }
  map.addSource(POINT_SOURCE_ID, { type: 'geojson', data })
  map.addLayer({ id: POINT_LAYER_IDS[0], type: 'fill', source: POINT_SOURCE_ID,
    paint: { 'fill-color': POINT_COLOR, 'fill-opacity': 0.08 } })
  map.addLayer({ id: POINT_LAYER_IDS[1], type: 'line', source: POINT_SOURCE_ID,
    paint: { 'line-color': POINT_COLOR, 'line-width': 1.2, 'line-dasharray': [2, 2] } })
}
